import { Flex, Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { RecurrentTaskStatus } from "__graphql__/types";
import { AssetCountEventType, AssetCountStatus } from "analytics/events";
import { routes } from "config/routes";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useEmployeeInfo } from "shared/hooks/useEmployeeInfo";
import { BatteryIcon, DateIcon, NewProfileIcon } from "ui/Icons/Icons";
import { Status } from "ui/Status/Status";
import { BodyM } from "ui/Typography/Typography";
import { formatDate } from "utils/formats";

import { useAssetManagementStore } from "../../stores/assetManagementStore";
import { ASSET_TYPE } from "../../utils/constants";

export function BatteryCountCard({
  count,
  employeeId,
  startDate,
  updatedAt,
  dueDate,
  taskId,
  status,
}: {
  count: null | number;
  employeeId: string | null;
  startDate: string;
  dueDate: string;
  taskId: string;
  status: RecurrentTaskStatus;
  updatedAt?: string | null;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { setCurrentTaskId, setCountSessionId } = useAssetManagementStore();

  const employeeName = useEmployeeInfo(employeeId)?.name;
  const isStatusDone = status === RecurrentTaskStatus.done;
  const displayedDate = updatedAt
    ? formatDate(updatedAt)
    : `${formatDate(startDate)} - ${formatDate(dueDate)}`;

  const goToCountPage = () => {
    const sessionId = crypto.randomUUID();
    setCountSessionId(sessionId);
    sendSegmentTrackEvent("assetCountStateUpdated", {
      asset_event_type: AssetCountEventType.AssetCountStarted,
      asset_count_process_id: taskId,
      asset_count_session_id: sessionId,
      status: isStatusDone ? AssetCountStatus.Done : AssetCountStatus.Pending,
      asset_type: ASSET_TYPE,
      number_of_asset_counted: count,
    });
    setCurrentTaskId(taskId);
    navigate(routes.inventory.batteryCount.scan);
  };

  return (
    <Box as="button" onClick={goToCountPage} p="s200" w="full" bg="white">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex gap="s100" direction="column">
          <Flex gap="s100" alignItems="center">
            <BatteryIcon boxSize="24px" />
            <BodyM noOfLines={1} wordBreak="break-all">
              {isStatusDone
                ? intl.formatMessage(
                    { id: "pages.inventory.battery-count-page.cards.counted" },
                    { count },
                  )
                : intl.formatMessage({ id: "pages.inventory.battery-count-page.cards.pending" })}
            </BodyM>
          </Flex>
          <Flex gap="s100" alignItems="center">
            <NewProfileIcon boxSize="24px" />
            <BodyM noOfLines={1} wordBreak="break-all">
              {employeeName || "-"}
            </BodyM>
          </Flex>
          <Flex gap="s100" alignItems="center">
            <DateIcon boxSize="24px" />
            <BodyM noOfLines={1} wordBreak="break-all">
              {displayedDate}
            </BodyM>
          </Flex>
        </Flex>
        <Flex alignItems="center" justifyContent="center">
          {isStatusDone ? (
            <Status
              textColor="white"
              bgColor="veggie.500"
              label={intl.formatMessage({
                id: "pages.inventory.battery-count-page.cards.status.done",
              })}
            />
          ) : (
            <Status
              textColor="white"
              bgColor="orange.400"
              label={intl.formatMessage({
                id: "pages.inventory.battery-count-page.cards.status.pending",
              })}
            />
          )}
        </Flex>
      </Flex>
    </Box>
  );
}
